import type { UserRoles } from "@/api/custom_types";

import OrderIcon from "@/assets/icons/Iconex/Light/Bag 3.svg?react";
import BellIcon from "@/assets/icons/Iconex/Light/Bell.svg?react";
import TransactionIcon from "@/assets/icons/Iconex/Light/Document.svg?react";
import HomeIcon from "@/assets/icons/Iconex/Light/Home.svg?react";
import LogoutIcon from "@/assets/icons/Iconex/Light/Logout.svg?react";
import SendIcon from "@/assets/icons/Iconex/Light/Send.svg?react";
import SettingsIcon from "@/assets/icons/Iconex/Light/Settings.svg?react";
import PaymentIcon from "@/assets/icons/Iconex/Light/Shield-1.svg?react";
import ShieldIcon from "@/assets/icons/Iconex/Light/Shield.svg?react";
import TransferIcon from "@/assets/icons/Iconex/Light/Swap 2.svg?react";
import UsersIcon from "@/assets/icons/Iconex/Light/Users.svg?react";
import { useOpenIntentDialog } from "@/components/auth/UserIntentModal";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { useGetProfile, useGetTicketsCount, useLogout } from "@/services";
import {
	INTENT_PERMISSIONS,
	currentUserAtom,
	userIntentAtom,
} from "@/store/login";
import {
	Button,
	Dialog,
	DialogContent,
	DialogOverlay,
	DialogPortal,
	Icon,
	Input,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	Typography,
	toast as uiToast,
} from "@st-marketplace/ui";

import { useLocation, useNavigate } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DashboardSidebarMenuItem, type MenuItemType } from "./components";

/** Polls open tickets for the admin and toasts when a new one comes in. */
function useOpenTicketsAlert(enabled: boolean) {
	const { t } = useTranslation();
	const { data } = useGetTicketsCount({ enabled });
	// The newest creation time, not the open count: a reopened ticket is not new.
	const previous = useRef<string | null | undefined>(undefined);

	useEffect(() => {
		if (data === undefined) return;
		const latest = data.latest_created_at;
		if (
			previous.current !== undefined &&
			latest &&
			latest !== previous.current
		) {
			uiToast.error(t("admin.tickets.newTicket"));
		}
		previous.current = latest;
	}, [data, t]);

	return data;
}

const TicketsBadge = ({ open, urgent }: { open: number; urgent: number }) => (
	<span className="relative flex">
		<span className="absolute inline-flex size-full animate-ping rounded-full bg-danger-text opacity-60" />
		<span
			className={cn(
				"relative inline-flex min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-semibold text-white",
				urgent ? "bg-danger-text" : "bg-danger-hover",
			)}
		>
			{open}
		</span>
	</span>
);

function useMenuItems(): MenuItemType[] {
	const { t } = useTranslation();
	const user = useAtomValue(currentUserAtom);
	const userIntent = useAtomValue(userIntentAtom);
	const userRole: UserRoles | undefined = user?.user_role;
	const { data: userProfile } = useGetProfile(
		{ path: { user_id: user?.id ?? "" } },
		{ enabled: Boolean(user?.id) && userRole !== "ADMIN" },
	);
	const ticketsCount = useOpenTicketsAlert(userRole === "ADMIN");
	const hideProxyMenu =
		userRole !== "ADMIN" && userProfile?.proxy_owner_type !== "PERSONAL";

	const stAdminMenu: MenuItemType[] = [
		{
			name: t("admin.menuDashboard"),
			icon: <HomeIcon />,
			route: "/st-admin/dashboard",
		},
		{
			name: t("admin.menuUsers"),
			icon: <UsersIcon />,
			route: "/st-admin/users",
		},
		{
			name: t("admin.menuTransferPlayers"),
			icon: <TransferIcon />,
			route: "/st-admin/transfer-players",
		},
		{
			name: t("admin.menuOrders"),
			icon: <OrderIcon />,
			route: "/orders",
		},
		{
			name: t("admin.menuSenders"),
			icon: <SendIcon />,
			route: "/senders",
		},
		{
			name: t("admin.menuTickets"),
			icon: <BellIcon />,
			route: "/st-admin/tickets",
			badge: ticketsCount?.open ? (
				<TicketsBadge open={ticketsCount.open} urgent={ticketsCount.urgent} />
			) : undefined,
		},
		{
			name: t("admin.menuPayments"),
			icon: <PaymentIcon />,
			route: "/st-admin/payments",
		},
		{
			name: t("admin.menuTransactions"),
			icon: <TransactionIcon />,
			route: "/st-admin/transactions",
		},
		{
			name: t("admin.menuManagement"),
			icon: <SettingsIcon />,
			route: "/st-admin/management",
		},
	];

	const marketMenu: (MenuItemType & {
		permissionKey: keyof typeof INTENT_PERMISSIONS.BOTH.navigation;
		route: string;
	})[] = [
		{
			name: t("dashboard.menuDashboard"),
			icon: <HomeIcon />,
			route: "/dashboard",
			permissionKey: "dashboard",
		},
		{
			name: t("dashboard.menuOrders"),
			icon: <OrderIcon />,
			route: "/orders",
			permissionKey: "orders",
		},
		{
			name: t("dashboard.menuSenders"),
			icon: <SendIcon />,
			route: "/senders",
			permissionKey: "senders",
		},
		{
			name: t("dashboard.menuProxy"),
			icon: <ShieldIcon />,
			route: "/proxy",
			permissionKey: "proxy",
		},
		{
			name: t("dashboard.menuBuyerRelations"),
			icon: <TransferIcon />,
			route: "/relations/buyer",
			permissionKey: "buyerRelations",
		},
		{
			name: t("dashboard.menuSellerRelations"),
			icon: <TransferIcon />,
			route: "/relations/seller",
			permissionKey: "sellerRelations",
		},
		{
			name: t("dashboard.payment"),
			icon: <PaymentIcon />,
			route: "/payment",
			permissionKey: "payment",
		},
		{
			name: t("dashboard.menuSetting"),
			icon: <SettingsIcon />,
			route: "/settings",
			permissionKey: "profile",
		},
	];

	if (userRole === "ADMIN") return stAdminMenu;

	if (userIntent) {
		const permissions = INTENT_PERMISSIONS[userIntent].navigation;
		return marketMenu.filter(
			(item) =>
				permissions[item.permissionKey] &&
				!(item.permissionKey === "proxy" && hideProxyMenu),
		);
	}

	return marketMenu.filter(
		(item) => !(item.permissionKey === "proxy" && hideProxyMenu),
	);
}

type CommandPaletteAction = {
	id: string;
	label: string;
	icon?: React.ReactNode;
	shortcut?: string;
	keywords?: string;
	onSelect: () => void;
};

function useCommandPaletteActions(params: {
	menuItems: MenuItemType[];
	isAdmin: boolean;
	t: ReturnType<typeof useTranslation>["t"];
	onOpenIntent: () => void;
	onLogout: () => void;
	onNavigate: (to: string) => void;
}): readonly CommandPaletteAction[] {
	const { menuItems, isAdmin, t, onOpenIntent, onLogout, onNavigate } = params;

	return useMemo(() => {
		const actions: CommandPaletteAction[] = menuItems.map((item) => ({
			id: `nav:${String(item.route)}`,
			label: item.name,
			icon: item.icon,
			keywords: String(item.route),
			onSelect: () => onNavigate(String(item.route)),
		}));

		if (!isAdmin) {
			actions.unshift({
				id: "action:market-intent",
				label: t("auth.userIntent.switchButton"),
				icon: <PaymentIcon />,
				shortcut: "Ctrl+I",
				keywords: "intent market mode",
				onSelect: onOpenIntent,
			});
		}

		actions.push({
			id: "action:logout",
			label: t("dashboard.buttonLogout"),
			icon: <LogoutIcon />,
			keywords: "sign out exit",
			onSelect: onLogout,
		});

		return actions;
	}, [isAdmin, menuItems, onLogout, onNavigate, onOpenIntent, t]);
}

function CommandPalette(props: {
	open: boolean;
	onClose: () => void;
	actions: readonly CommandPaletteAction[];
	placeholder: string;
	emptyText: string;
	helpText: string;
}) {
	const { open, onClose, actions, placeholder, emptyText, helpText } = props;

	const [query, setQuery] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const listRef = useRef<HTMLDivElement | null>(null);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return actions;

		return actions.filter((a) => {
			const hay = `${a.label} ${a.keywords ?? ""}`.toLowerCase();
			return hay.includes(q);
		});
	}, [actions, query]);

	useEffect(() => {
		if (!open) {
			setQuery("");
			setActiveIndex(0);
			return;
		}
		setQuery("");
		setActiveIndex(0);
		queueMicrotask(() => inputRef.current?.focus());
	}, [open]);

	useEffect(() => {
		setActiveIndex((i) => Math.min(i, Math.max(filtered.length - 1, 0)));
	}, [filtered.length]);

	useEffect(() => {
		const el = listRef.current;
		if (!el) return;
		const activeEl = el.children[activeIndex] as HTMLElement | undefined;
		if (activeEl) {
			activeEl.scrollIntoView({ block: "nearest" });
		}
	}, [activeIndex]);

	const run = useCallback(
		(action: CommandPaletteAction | undefined) => {
			if (!action) return;
			onClose();
			action.onSelect();
		},
		[onClose],
	);

	const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
			return;
		}
		if (e.key === "ArrowUp") {
			e.preventDefault();
			setActiveIndex((i) => Math.max(i - 1, 0));
			return;
		}
		if (e.key === "Enter") {
			e.preventDefault();
			run(filtered[activeIndex]);
			return;
		}
		if (e.key === "Escape") {
			e.preventDefault();
			onClose();
		}
	};

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogPortal>
				<DialogOverlay className="backdrop-blur-sm" />
				<DialogContent className="top-24! translate-y-0! max-w-140! rounded-3xl!">
					<div className="flex flex-col gap-3 p-6">
						<Input
							ref={inputRef}
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							onKeyDown={onKeyDown}
							placeholder={placeholder}
							autoComplete="off"
						/>

						<div className="border border-secondary-border rounded-3xl overflow-hidden">
							<div
								ref={listRef}
								className="max-h-90 overflow-y-auto"
								role="listbox"
							>
								{filtered.length === 0 ? (
									<div className="px-4 py-4">
										<Typography
											variant="body-sm"
											as="p"
											className="text-secondary-text"
										>
											{emptyText}
										</Typography>
									</div>
								) : (
									filtered.map((action, idx) => (
										<button
											type="button"
											key={action.id}
											role="option"
											aria-selected={idx === activeIndex}
											className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
												idx === activeIndex
													? "bg-primary-fill/10 text-primary-text"
													: "text-secondary-text hover:bg-background-100"
											}`}
											onMouseEnter={() => setActiveIndex(idx)}
											onClick={() => run(action)}
										>
											{action.icon ? (
												<span className="flex items-center justify-center w-5 h-5 shrink-0 [&>svg]:w-5 [&>svg]:h-5">
													{action.icon}
												</span>
											) : null}
											<span className="flex-1 text-body-md">
												{action.label}
											</span>
											{action.shortcut ? (
												<span className="text-caption font-semibold text-secondary-text">
													{action.shortcut}
												</span>
											) : null}
										</button>
									))
								)}
							</div>
						</div>

						<Typography
							variant="caption"
							as="p"
							className="text-secondary-text"
						>
							{helpText}
						</Typography>
					</div>
				</DialogContent>
			</DialogPortal>
		</Dialog>
	);
}

type SidebarMetaTileProps = {
	label: string;
	value: string;
	onClick: () => void;
};

function SidebarMetaTile({
	label,
	value,
	onClick,
}: SidebarMetaTileProps): React.ReactElement {
	return (
		<button
			type="button"
			onClick={onClick}
			className="group flex h-12 w-full items-center justify-between gap-3 rounded-lg border border-secondary-border bg-background-100/50 px-3 text-start transition-colors hover:border-primary-border hover:bg-background-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-border"
		>
			<span className="flex min-w-0 items-baseline gap-2">
				<Typography
					variant="caption"
					as="span"
					className="shrink-0 text-secondary-icon"
				>
					{label}
				</Typography>
				<Typography
					variant="body-sm"
					as="span"
					className="truncate font-semibold text-secondary-text"
				>
					{value}
				</Typography>
			</span>
			<span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-background-200 text-secondary-icon transition-colors group-hover:text-primary-fill [&>svg]:size-4">
				<Icon name="arrow-left-right-line" />
			</span>
		</button>
	);
}

function DashboardLayoutSidebar() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const location = useLocation();
	const user = useAtomValue(currentUserAtom);
	const userIntent = useAtomValue(userIntentAtom);

	const openIntentDialog = useOpenIntentDialog();
	const [paletteOpen, setPaletteOpen] = useState(false);

	const logoutMutation = useLogout();
	const menuItems = useMenuItems();

	const isAdmin = user?.user_role === "ADMIN";
	const title = isAdmin ? t("admin.title") : t("logo.name");
	const intentLabel = userIntent
		? t(`auth.userIntent.${userIntent}.title`)
		: "—";

	const handleLogout = useCallback(() => {
		logoutMutation.mutate();
	}, [logoutMutation]);

	const handleNavigate = useCallback(
		(to: string) => {
			navigate({ to });
		},
		[navigate],
	);

	const commandActions = useCommandPaletteActions({
		menuItems,
		isAdmin,
		t,
		onOpenIntent: () => openIntentDialog(),
		onLogout: handleLogout,
		onNavigate: handleNavigate,
	});

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			const key = e.key.toLowerCase();
			const mod = e.ctrlKey || e.metaKey;

			if (mod && key === "k") {
				e.preventDefault();
				setPaletteOpen((v) => !v);
				return;
			}

			if (!isAdmin && mod && key === "i") {
				e.preventDefault();
				openIntentDialog();
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [isAdmin, openIntentDialog]);

	return (
		<>
			<div
				className="w-60 transition-all overflow-hidden fixed px-4 bg-background-50 border-e border-secondary-border shadow-sm z-30 whitespace-nowrap flex flex-col"
				style={{
					top: 0,
					height: "100vh",
					paddingBottom: "calc(32px + env(safe-area-inset-bottom))",
				}}
			>
				<div className="h-24 flex items-center px-3">
					<img
						src="/snipe-transfer-logo.png"
						alt="SnipeTransfer"
						className="size-12 shrink-0 object-contain"
					/>
					<Typography
						variant="body-lg"
						as="p"
						className="menu-text ms-3 font-semibold tracking-tight"
					>
						{isAdmin ? (
							title
						) : (
							<>
								<span className="bg-gradient-to-r from-[#C7FF5B] via-[#12E7D6] to-[#168BFF] bg-clip-text text-transparent">
									Snipe
								</span>
								<span className="text-secondary-text">Transfer</span>
							</>
						)}
					</Typography>
				</div>

				{!isAdmin && (
					<div className="px-3 pb-6">
						<Tooltip>
							<TooltipTrigger asChild>
								<SidebarMetaTile
									label={t("auth.userIntent.modeLabel")}
									value={intentLabel}
									onClick={() => openIntentDialog()}
								/>
							</TooltipTrigger>
							<TooltipContent side="right">
								{t("auth.userIntent.switchButton")}
							</TooltipContent>
						</Tooltip>
					</div>
				)}

				<hr className="border-secondary-border mb-7" />

				<div className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto pe-1">
					{menuItems.map((item) => (
						<Tooltip key={String(item.route)}>
							<TooltipTrigger asChild>
								<div>
									<DashboardSidebarMenuItem
										item={item}
										isActive={location.pathname === item.route}
										onClick={() => handleNavigate(String(item.route))}
									/>
								</div>
							</TooltipTrigger>
							<TooltipContent side="right">{item.name}</TooltipContent>
						</Tooltip>
					))}
				</div>

				<hr className="border-secondary-border mb-6" />

				<Button
					variant="text"
					color="danger"
					size="lg"
					onClick={handleLogout}
					className="w-full justify-start gap-3"
				>
					<LogoutIcon />
					{t("dashboard.buttonLogout")}
				</Button>
			</div>

			<CommandPalette
				open={paletteOpen}
				onClose={() => setPaletteOpen(false)}
				actions={commandActions}
				placeholder={t("dashboard.commandPalettePlaceholder")}
				emptyText={t("dashboard.commandPaletteNoResults")}
				helpText={t("dashboard.commandPaletteHelpText")}
			/>
		</>
	);
}

export default DashboardLayoutSidebar;
